import { Component, ErrorInfo } from 'react';
import { errorCount as getErrorCount, errorTimestamp as getErrorTimestamp, setErrorCookie } from '~/store/cookies';
import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from './error-handling/error-reporter-domain';

const reloadPage = () => {
	(window as Window).location = '/';
};

type Props = {
	children: React.ReactNode;
	errorTimeThreshold?: number;
	ignoreErrors?: boolean;
	maxReloads?: number;
	reload?: boolean;
	delay?: number;
	domain?: ErrorReporterDomain;
	reportErrorCallback?: (error: Error, info: ErrorInfo) => void;
	fallback?: React.ReactNode;
};

type State = {
	hasError: boolean;
};

/**
 * Root Error Boundarey is meant for root/app.js top level components. For other use cases, please use ErrorBoundary in @comeon/error-reporter
 */
class RootErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const {
			delay = 0,
			errorTimeThreshold = 4000,
			maxReloads = 2,
			reload = false,
			ignoreErrors,
			reportErrorCallback
		} = this.props;

		if (ignoreErrors) {
			return;
		}

		if (reportErrorCallback) {
			reportErrorCallback(error, errorInfo);
		} else {
			reportException(error, { domain: this.props.domain ?? ErrorReporterDomain.UNKNOWN, extra: { errorInfo } });
		}

		if (!reload) {
			return;
		}

		const errorCount = getErrorCount() || 1;
		const errorTimestamp = getErrorTimestamp() || new Date(0); // set a default date

		// Check if the error occurred within the threshold to determine if it is looping reloads
		const nextErrorDate = new Date(errorTimestamp.getTime() + errorTimeThreshold);

		const isLooping = new Date() < nextErrorDate;

		// Stop looping if it has already loaded the set time
		if (isLooping && maxReloads < errorCount) {
			return;
		}

		const resetErrorCookie = !isLooping;
		setErrorCookie(resetErrorCookie);

		// Update error count and date in cookie
		setTimeout(reloadPage, delay);
	}

	render() {
		const { ignoreErrors, children, fallback } = this.props;
		const { hasError } = this.state;

		if (hasError && fallback) {
			return fallback;
		}

		if (hasError && !ignoreErrors) {
			return null;
		}

		return children;
	}
}

export default RootErrorBoundary;
